/**
 * Tab Navigation Component
 * UX Redesign: Changed tab names and icons for better business context
 * - Dashboard → Overview (Обзор)
 * - Billing → Costs (Расходы) with Wallet icon
 * - Settings → Delivery (Доставка) with Send icon
 */
import { LayoutDashboard, FileText, Lock, MessagesSquare } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import type { Tab } from '../types';
import type { UnifiedSection } from '@/components/dashboards/unified/unified-search';
import { sectionLockedByPlan } from '@/components/dashboards/unified/layout';
import { TrialTabMark } from '@/components/dashboards/unified/TrialTabMark';
import { useFeatureFlagsSafe } from '@/contexts/FeatureFlagsContext';
import { EmbeddedControls } from './EmbeddedControls';
import { EmbeddedAssistantButton } from '@/components/assistant/EmbeddedAssistantButton';

/**
 * The unified dashboard's tabs, drawn in this strip in place of «Обзор»
 * (#1337): the six tabs replace the Overview rather than sit in a second row
 * under it. `active` — the one on screen while the Overview is open. With no
 * section to offer the strip keeps «Обзор» itself.
 */
export interface DashboardSectionTabs {
  sections: readonly UnifiedSection[];
  /**
   * «My access» is still on its way: a grant may narrow the six, so none is
   * drawn yet — a neutral placeholder stands in, and nothing is hidden by a
   * set that is not known.
   */
  pending?: boolean;
  active: UnifiedSection | null;
  onSelect: (section: UnifiedSection) => void;
}

interface TabNavigationProps {
  activeTab: Tab;
  onTabChange: (tab: Tab) => void;
  /**
   * May the subject open this tab (`embeddedTabAllowed`, #1645): the role —
   * «Голос клиента» не открыт оператору (#848) — or, for a grant holder, its
   * grant (Records needs `records.read`).
   */
  isTabAllowed: (tab: Tab) => boolean;
  /** The unified dashboard is on: its tabs instead of «Обзор». */
  dashboardSections?: DashboardSectionTabs;
}

const SECTION_VALUE = 'dashboard:';

export function TabNavigation({
  activeTab,
  onTabChange,
  isTabAllowed,
  dashboardSections,
}: TabNavigationProps) {
  const { t } = useTranslation(['dashboard', 'common']);
  // A tab above the plan («Продажи», #1339): marked here, before it is opened
  // — the session carries the verdict; the tab itself then shows the plan
  // screen. A session with no verdict marks nothing: the route decides.
  const { gate } = useFeatureFlagsSafe();
  // «Голос клиента» is one of the six: the separate tab is not drawn twice —
  // its full section stays one click away, the card's «Отчёт →». A grant
  // without it keeps the separate tab. Until the set is known neither is
  // drawn — the placeholder covers the slot, so the tab is not drawn and then
  // taken back.
  const voiceAsSection = dashboardSections?.sections.includes('voc') ?? false;
  const voiceTab =
    isTabAllowed('voice') && !dashboardSections?.pending && !voiceAsSection;
  // What renders is what is lit. `tab=voice` is the full report, a view of its
  // own (a link from the card, an email, an old bookmark): none of the six is
  // lit for it — like Objections — so the section's trigger is not a dead
  // click on the one already lit.
  const value =
    dashboardSections && activeTab === 'dashboard' && dashboardSections.active
      ? `${SECTION_VALUE}${dashboardSections.active}`
      : activeTab;
  const onValueChange = (next: string) => {
    if (dashboardSections && next.startsWith(SECTION_VALUE)) {
      const section = dashboardSections.sections.find(
        (candidate) => `${SECTION_VALUE}${candidate}` === next
      );
      if (section) dashboardSections.onSelect(section);
      return;
    }
    onTabChange(next as Tab);
  };

  return (
    <div className="border-b border-border bg-bg-surface sticky top-0 z-10">
      <div className="px-4 flex gap-1 items-center">
        {/* The shared Tabs primitive owns the height and the states (#1379): no
            call-site sizing, no hover token of our own. */}
        {/* Manual activation: with no trigger for the active view (Objections,
            Settings), Tab-ing into the bar must not switch to the first one and
            drop the view's filters — a click or Enter does. */}
        <Tabs
          value={value}
          onValueChange={onValueChange}
          activationMode="manual"
          className={dashboardSections ? 'min-w-0 flex-1' : undefined}
        >
          <TabsList
            variant="underline"
            className="flex-wrap gap-1 border-b-0 bg-transparent px-0"
          >
            {dashboardSections?.pending ? (
              <div
                role="presentation"
                aria-busy="true"
                data-testid="embedded-sections-pending"
                className="flex items-center gap-4 px-3 py-2"
              >
                <Skeleton width={72} height={16} />
                <Skeleton width={96} height={16} />
                <Skeleton width={72} height={16} />
              </div>
            ) : dashboardSections && dashboardSections.sections.length > 0 ? (
              dashboardSections.sections.map((section) => (
                <TabsTrigger
                  key={section}
                  variant="underline"
                  value={`${SECTION_VALUE}${section}`}
                >
                  {t(`unified.tabs.${section}`)}
                  {sectionLockedByPlan(section, gate) ? (
                    <Lock
                      data-testid="embedded-tab-locked"
                      aria-label={t('unified.card.notOnPlan')}
                      className="ml-1 h-3 w-3 text-fg-muted"
                    />
                  ) : (
                    <TrialTabMark section={section} />
                  )}
                </TabsTrigger>
              ))
            ) : (
              // «Обзор» is also what stands when the preview is on and the
              // viewer is offered none of the six (a grant without
              // `aggregates.*`, or «my access» could not be read): without it
              // there is no way back to the dashboard from Records or Settings,
              // and the panel under it says why there is nothing.
              <TabsTrigger variant="underline" value="dashboard">
                <LayoutDashboard className="h-4 w-4" />
                {t('embedded.overview')}
              </TabsTrigger>
            )}
            {isTabAllowed('records') && (
              <TabsTrigger variant="underline" value="records">
                <FileText className="h-4 w-4" />
                {t('embedded.records')}
              </TabsTrigger>
            )}
            {/* Кнопка — при любом тарифе: закрытый раздел сам говорит, что он на
                Pro, а спрятанный не узнал бы никто (как пометка тарифа во
                всплывающем окне релиза). */}
            {voiceTab && (
              <TabsTrigger variant="underline" value="voice">
                <MessagesSquare className="h-4 w-4" />
                {t('voicePage.title')}
              </TabsTrigger>
            )}
          </TabsList>
        </Tabs>
        <EmbeddedAssistantButton />
        {/* Language + Theme controls on the right */}
        <EmbeddedControls />
      </div>
    </div>
  );
}
